import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import PhotoGallery from '@/components/PhotoGallery';
import { PermissionGate } from '@/components/PermissionGate';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import { 
  ArrowLeft, 
  Send, 
  Download,
  Copy,
  CopyPlus,
  CheckCircle,
  XCircle,
  FileText,
  Edit2,
  Edit,
  Trash2,
  Link2,
  ExternalLink,
  Receipt,
  Eye,
  Briefcase,
  Calendar,
  Scissors,
  Merge,
  Bell,
  BellOff,
  Clock,
  Loader2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import PDFPreviewModal from '@/components/PDFPreviewModal';
import SplitEstimateModal from '@/components/SplitEstimateModal';
import CombineEstimatesModal from '@/components/CombineEstimatesModal';
import EstimateQA from '@/components/EstimateQA';

// Smart post-approval actions: deposit-aware workflow
const ApprovedEstimateActions = ({ 
  estimate, navigate, setShowConvertToJobModal, 
  handleOpenResendModal, handleConvertToInvoice, getDeclinedItems, t 
}) => {
  const hasDeposit = estimate.deposit_required && estimate.deposit_value > 0;
  const hasInvoice = !!estimate.converted_to_invoice_id;
  const hasJob = !!estimate.converted_to_job_id;
  
  // Check if invoice exists and is accessible (null = still loading)
  const [invoiceValid, setInvoiceValid] = useState(hasInvoice ? null : true);
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  
  useEffect(() => {
    const checkInvoice = async () => {
      if (!hasInvoice) { setInvoiceValid(true); return; }
      try {
        const res = await apiHelpers.getInvoice(estimate.org_id, estimate.converted_to_invoice_id);
        setInvoiceValid(true);
        setInvoiceStatus(res.data?.status || null);
      } catch {
        setInvoiceValid(false);
      }
    };
    checkInvoice();
  }, [hasInvoice, estimate.converted_to_invoice_id, estimate.org_id]);
  
  // Check if job exists and is accessible (null = still loading)
  const [jobValid, setJobValid] = useState(hasJob ? null : true);
  
  useEffect(() => {
    const checkJob = async () => {
      if (!hasJob) { setJobValid(true); return; }
      try {
        await apiHelpers.getJob(estimate.org_id, estimate.converted_to_job_id);
        setJobValid(true);
      } catch {
        setJobValid(false);
      }
    };
    checkJob();
  }, [hasJob, estimate.converted_to_job_id, estimate.org_id]);
  
  // While async checks are loading, show nothing to prevent flicker
  const isCheckingRecords = (hasInvoice && invoiceValid === null) || (hasJob && jobValid === null);
  
  const depositPaid = invoiceStatus === 'paid' || invoiceStatus === 'partial';
  
  // Show a subtle loading indicator while checking linked records
  if (isCheckingRecords) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm h-12 px-3" data-testid="approved-actions-loading">
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        Checking linked records...
      </div>
    );
  }
  
  // If estimate has been combined with others, lock conversion actions
  if (estimate.is_combined || estimate.combined_into_invoice_id || estimate.combined_into_job_id) {
    const targetInvoice = estimate.combined_into_invoice_id;
    const targetJob = estimate.combined_into_job_id;
    return (
      <div className="space-y-2" data-testid="combined-locked-actions">
        <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 border border-purple-200 px-3 py-2 rounded-xl">
          <Merge className="w-4 h-4 flex-shrink-0" />
          <span>This estimate has been combined. Open the combined record to make changes.</span>
        </div>
        {targetInvoice && (
          <Button
            onClick={() => navigate(`/invoices/${targetInvoice}`)}
            variant="outline"
            className="h-12 w-full text-purple-700 border-purple-200 hover:bg-purple-50"
            data-testid="view-combined-invoice-btn"
          >
            <Receipt className="w-5 h-5 mr-2" />
            View Combined Invoice
          </Button>
        )}
        {targetJob && (
          <Button
            onClick={() => navigate(`/jobs/${targetJob}`)}
            variant="outline"
            className="h-12 w-full text-purple-700 border-purple-200 hover:bg-purple-50"
            data-testid="view-combined-job-btn"
          >
            <Briefcase className="w-5 h-5 mr-2" />
            View Combined Job
          </Button>
        )}
      </div>
    );
  }
  
  return (
    <>
      {/* Step 1: If deposit required and no invoice yet → Create Invoice first */}
      {hasDeposit && !hasInvoice && (
        <Button
          onClick={handleConvertToInvoice}
          className="bg-green-600 hover:bg-green-700 text-white h-12"
          data-testid="create-deposit-invoice-btn"
        >
          <Receipt className="w-5 h-5 mr-2" />
          Create Deposit Invoice
        </Button>
      )}
      
      {/* Step 2: If deposit invoice exists but not paid → show View Invoice */}
      {hasDeposit && hasInvoice && invoiceValid && !depositPaid && (
        <Button
          onClick={() => navigate(`/invoices/${estimate.converted_to_invoice_id}`)}
          variant="outline"
          className="h-12 text-amber-600 border-amber-200 hover:bg-amber-50"
          data-testid="view-deposit-invoice-btn"
        >
          <Receipt className="w-5 h-5 mr-2" />
          View Deposit Invoice (Awaiting Payment)
        </Button>
      )}
      
      {/* Step 3: If no deposit required OR deposit paid → show Create Job */}
      {!hasJob && (!hasDeposit || depositPaid) && (
        <Button
          onClick={() => setShowConvertToJobModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white h-12"
          data-testid="convert-to-job-btn"
        >
          <Briefcase className="w-5 h-5 mr-2" />
          {t('create_job')}
        </Button>
      )}
      
      {/* If no deposit and no invoice → show Create Invoice (for post-work billing) */}
      {!hasDeposit && !hasInvoice && (
        <Button
          onClick={handleConvertToInvoice}
          variant="outline"
          className="h-12"
          data-testid="convert-to-invoice-btn"
        >
          <Receipt className="w-5 h-5 mr-2" />
          {t('create_invoice')}
        </Button>
      )}
      
      {/* View existing job */}
      {hasJob && jobValid && (
        <Button
          onClick={() => navigate(`/jobs/${estimate.converted_to_job_id}`)}
          variant="outline"
          className="h-12 text-blue-600 border-blue-200 hover:bg-blue-50"
          data-testid="view-job-btn"
        >
          <Briefcase className="w-5 h-5 mr-2" />
          View Job
        </Button>
      )}
      
      {/* View existing invoice (non-deposit case) */}
      {!hasDeposit && hasInvoice && invoiceValid && (
        <Button
          onClick={() => navigate(`/invoices/${estimate.converted_to_invoice_id}`)}
          variant="outline"
          className="h-12 text-green-600 border-green-200 hover:bg-green-50"
          data-testid="view-invoice-btn"
        >
          <Receipt className="w-5 h-5 mr-2" />
          View Invoice
        </Button>
      )}
      
      {/* Deposit paid → also show View Invoice */}
      {hasDeposit && hasInvoice && invoiceValid && depositPaid && (
        <Button
          onClick={() => navigate(`/invoices/${estimate.converted_to_invoice_id}`)}
          variant="outline"
          className="h-12 text-green-600 border-green-200 hover:bg-green-50"
          data-testid="view-invoice-btn"
        >
          <Receipt className="w-5 h-5 mr-2" />
          View Invoice
        </Button>
      )}
      
      {/* FIX 5: Recovery — if invoice was deleted, allow recreation */}
      {hasInvoice && !invoiceValid && (
        <Button
          onClick={handleConvertToInvoice}
          className="bg-amber-600 hover:bg-amber-700 text-white h-12"
          data-testid="recreate-invoice-btn"
        >
          <Receipt className="w-5 h-5 mr-2" />
          Recreate Invoice (Missing)
        </Button>
      )}
      
      {/* FIX 5: Recovery — if job was deleted, allow recreation */}
      {hasJob && !jobValid && (
        <Button
          onClick={() => setShowConvertToJobModal(true)}
          className="bg-amber-600 hover:bg-amber-700 text-white h-12"
          data-testid="recreate-job-btn"
        >
          <Briefcase className="w-5 h-5 mr-2" />
          Recreate Job (Missing)
        </Button>
      )}
      
      {/* Resend Declined Items Button */}
      {getDeclinedItems().length > 0 && (
        <Button
          onClick={handleOpenResendModal}
          variant="outline"
          className="h-12 text-amber-600 border-amber-200 hover:bg-amber-50"
          data-testid="resend-declined-btn"
        >
          <CopyPlus className="w-5 h-5 mr-2" />
          Resend Declined ({getDeclinedItems().length})
        </Button>
      )}
    </>
  );
};

// Separate component to avoid hydration warnings from tooling wrapping dynamic content
const LineItemsTableBody = ({ items }) => (
  <tbody suppressHydrationWarning>
    {items.map((item, index) => (
      <tr key={index} className="border-b border-gray-100" suppressHydrationWarning>
        <td className="py-3">
          <p className="font-medium">{item.name}</p>
          {item.description && (
            <p className="text-sm text-muted-foreground">{item.description}</p>
          )}
        </td>
        <td className="text-right py-3">{item.qty} {item.unit}</td>
        <td className="text-right py-3">${item.rate?.toFixed(2)}</td>
        <td className="text-right py-3 font-medium">${item.total?.toFixed(2)}</td>
      </tr>
    ))}
  </tbody>
);


// Follow-Up Reminders Panel for sent estimates
const FollowUpPanel = ({ estimate, onUpdate }) => {
  const { organization, user } = useAuth();
  const [followUps, setFollowUps] = useState([]);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (organization?.id && estimate?.id) {
      apiHelpers.getEstimateFollowUps(organization.id, estimate.id)
        .then(res => setFollowUps(res.data || []))
        .catch(() => {});
    }
  }, [organization?.id, estimate?.id]);

  const handleSendNow = async () => {
    setIsSending(true);
    try {
      const res = await apiHelpers.sendManualFollowUp(organization.id, estimate.id, user.id, user.name);
      toast.success(res.data.message);
      const updated = await apiHelpers.getEstimateFollowUps(organization.id, estimate.id);
      setFollowUps(updated.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setIsSending(false);
    }
  };

  const handleTogglePause = async () => {
    try {
      if (estimate.follow_ups_paused) {
        await apiHelpers.resumeFollowUps(organization.id, estimate.id, user.id);
        toast.success('Follow-ups resumed');
      } else {
        await apiHelpers.pauseFollowUps(organization.id, estimate.id, user.id);
        toast.success('Follow-ups paused');
      }
      onUpdate?.();
    } catch {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="follow-up-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" /> Follow-Up Reminders
        </h2>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="outline"
            onClick={handleTogglePause}
            className={`h-7 text-xs ${estimate.follow_ups_paused ? 'text-green-600 border-green-300' : 'text-amber-600 border-amber-300'}`}
            data-testid="toggle-follow-up-pause"
          >
            {estimate.follow_ups_paused ? <><Bell className="w-3 h-3 mr-1" /> Resume</> : <><BellOff className="w-3 h-3 mr-1" /> Pause</>}
          </Button>
          <Button
            size="sm" onClick={handleSendNow} disabled={isSending}
            className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="send-follow-up-now"
          >
            {isSending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
            Send Now
          </Button>
        </div>
      </div>

      {estimate.follow_ups_paused && (
        <div className="p-2 mb-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
          Auto follow-ups are paused for this estimate
        </div>
      )}

      {followUps.length === 0 ? (
        <p className="text-sm text-muted-foreground">No follow-ups sent yet</p>
      ) : (
        <div className="space-y-2">
          {followUps.map((fu, idx) => (
            <div key={fu.id || idx} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-gray-50 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span>Reminder #{fu.reminder_number}</span>
                <span className="text-muted-foreground">→ {fu.email_sent_to}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-1.5 py-0.5 rounded ${fu.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {fu.status}
                </span>
                <span className="text-xs text-muted-foreground">
                  {fu.sent_at ? formatDistanceToNow(new Date(fu.sent_at), { addSuffix: true }) : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EstimateDetailPage = () => {
  const { estimateId } = useParams();
  const { organization, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  
  const [estimate, setEstimate] = useState(null);
  const [client, setClient] = useState(null);
  const [linkedJob, setLinkedJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [publicUrl, setPublicUrl] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showConvertToJobModal, setShowConvertToJobModal] = useState(false);
  const [jobStartDate, setJobStartDate] = useState('');
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendItems, setResendItems] = useState([]);
  const [isCreatingRevision, setIsCreatingRevision] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showCombineModal, setShowCombineModal] = useState(false);

  // Get declined items for resend
  const getDeclinedItems = () => {
    if (estimate?.display_mode !== 'simple' || !estimate?.simple_pricing) return [];
    return estimate.simple_pricing.filter(item => item.is_approved === false);
  };
  
  // Get approved items count
  const getApprovedItemsCount = () => {
    if (estimate?.display_mode !== 'simple' || !estimate?.simple_pricing) return 0;
    return estimate.simple_pricing.filter(item => item.is_approved !== false).length;
  };

  useEffect(() => {
    loadEstimate();
  }, [estimateId, organization?.id]);

  const loadEstimate = async () => {
    if (!organization?.id || !estimateId) return;
    
    try {
      const response = await apiHelpers.getEstimate(organization.id, estimateId);
      setEstimate(response.data);
      
      // Load client
      const clientRes = await apiHelpers.getClient(organization.id, response.data.client_id);
      setClient(clientRes.data);
      
      // Load linked job if exists
      if (response.data.converted_to_job_id) {
        try {
          const jobRes = await apiHelpers.getJob(organization.id, response.data.converted_to_job_id);
          setLinkedJob(jobRes.data);
        } catch (e) {
          console.log('Linked job not found');
        }
      }
    } catch (error) {
      console.error('Failed to load estimate:', error);
      toast.error('Failed to load estimate');
      navigate('/estimates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEstimate = async () => {
    setIsSending(true);
    try {
      const response = await apiHelpers.sendEstimate(
        organization.id,
        estimateId,
        user.id,
        user.name,
        true // include PDF
      );
      
      const baseUrl = window.location.origin;
      const fullUrl = `${baseUrl}${response.data.public_url}`;
      setPublicUrl(fullUrl);
      
      setEstimate(prev => ({ ...prev, status: 'sent', sent_at: new Date().toISOString() }));
      
      if (response.data.email_sent) {
        toast.success('Estimate sent to client!', {
          description: 'Email delivered with approval link',
          action: {
            label: 'Copy Link',
            onClick: () => copyLink(fullUrl)
          }
        });
      } else {
        toast.success('Estimate ready to share', {
          description: 'Client has no email - use the link to share',
          action: {
            label: 'Copy Link',
            onClick: () => copyLink(fullUrl)
          }
        });
      }
    } catch (error) {
      console.error('Failed to send estimate:', error);
      toast.error('Failed to send estimate');
    } finally {
      setIsSending(false);
    }
  };

  const copyLink = async (url) => {
    const linkToCopy = url || publicUrl || `${window.location.origin}/estimate/${estimate.public_view_token}`;
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(linkToCopy);
      } else {
        // Fallback for non-secure contexts or older browsers
        const textArea = document.createElement('textarea');
        textArea.value = linkToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Link copied to clipboard!');
    } catch (error) {
      // Final fallback: show the link for manual copy
      toast.info(`Copy this link: ${linkToCopy}`, { duration: 8000 });
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await apiHelpers.getEstimatePdf(organization.id, estimateId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `estimate-${estimateId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Open resend modal with declined items
  const handleOpenResendModal = () => {
    const declined = getDeclinedItems();
    // Allow editing - deep copy
    setResendItems(declined.map(item => ({ 
      ...item, 
      id: item.id || crypto.randomUUID(),
      title: item.title,
      price: item.price 
    })));
    setShowResendModal(true);
  };

  // Create revision estimate with declined items
  const handleCreateRevisionEstimate = async () => {
    if (resendItems.length === 0) {
      toast.error('No items to resend');
      return;
    }
    
    setIsCreatingRevision(true);
    try {
      // Create a new estimate as a revision with just the declined items
      const revisionData = {
        client_id: estimate.client_id,
        service_type: estimate.service_type,
        project_title: estimate.project_title ? `${estimate.project_title} (Revision)` : 'Revised Estimate',
        work_types: estimate.work_types || [],
        job_address: estimate.job_address,
        display_mode: 'simple',
        simple_materials: estimate.simple_materials,
        simple_process: estimate.simple_process,
        simple_pricing: resendItems.map(item => ({
          id: crypto.randomUUID(),
          title: item.title,
          price: parseFloat(item.price) || 0,
          is_approved: null // Reset approval status
        })),
        tax_rate: estimate.tax_rate,
        require_deposit: estimate.require_deposit,
        deposit_type: estimate.deposit_type,
        deposit_value: estimate.deposit_value,
        valid_days: estimate.valid_days,
        internal_notes: `Revision of estimate ${estimateId.slice(0, 8).toUpperCase()}. Original declined items being re-submitted.`,
        // Link to parent estimate
        parent_estimate_id: estimateId
      };
      
      const response = await apiHelpers.createEstimate(organization.id, revisionData, user.id, user.name);
      
      toast.success('Revision estimate created!', {
        description: 'You can now edit and send it to the customer.'
      });
      
      setShowResendModal(false);
      navigate(`/estimates/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create revision:', error);
      toast.error('Failed to create revision estimate', {
        description: error.response?.data?.detail || 'Please try again'
      });
    } finally {
      setIsCreatingRevision(false);
    }
  };

  // Update resend item
  const updateResendItem = (index, field, value) => {
    setResendItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleConvertToInvoice = async () => {
    try {
      const response = await apiHelpers.convertToInvoice(
        organization.id,
        estimateId,
        user.id,
        user.name
      );
      
      toast.success('Invoice created!');
      navigate(`/invoices/${response.data.id}`);
    } catch (error) {
      console.error('Failed to convert to invoice:', error);
      toast.error('Failed to create invoice', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const handleConvertToJob = async () => {
    try {
      const response = await apiHelpers.convertEstimateToJob(
        estimateId,
        organization.id,
        jobStartDate || null
      );
      
      toast.success('Job created from estimate!');
      setShowConvertToJobModal(false);
      
      // Reload estimate to get the linked job ID
      await loadEstimate();
      
      // Optionally navigate to the job
      if (response.data.job_id) {
        navigate(`/jobs/${response.data.job_id}`);
      }
    } catch (error) {
      console.error('Failed to convert to job:', error);
      toast.error('Failed to create job', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const handleDelete = async () => {
    const confirmed = await showConfirm({
      title: 'Delete Estimate?',
      description: 'This action cannot be undone. The estimate will be permanently removed.',
      variant: 'danger',
      confirmText: 'Delete',
      destructive: true
    });
    
    if (!confirmed) return;
    
    try {
      await apiHelpers.deleteEstimate(organization.id, estimateId, user.id);
      toast.success('Estimate deleted');
      navigate('/estimates');
    } catch (error) {
      console.error('Failed to delete estimate:', error);
      const detail = error?.response?.data?.detail || 'Failed to delete estimate';
      toast.error(detail);
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await apiHelpers.duplicateEstimate(organization.id, estimateId, user.id, user.name);
      toast.success('Estimate duplicated');
      navigate(`/estimates/${response.data.id}`);
    } catch (error) {
      console.error('Failed to duplicate estimate:', error);
      toast.error('Failed to duplicate estimate');
    }
  };

  const statusColors = {
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    declined: 'bg-red-100 text-red-700',
    expired: 'bg-yellow-100 text-yellow-700'
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!estimate) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="estimate-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/estimates')}
            className="p-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl md:text-3xl font-bold">
                Estimate #{estimateId.slice(0, 8).toUpperCase()}
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${statusColors[estimate.status]}`}>
                {estimate.status.toUpperCase()}
              </span>
            </div>
            <p className="text-muted-foreground">
              Created {formatDistanceToNow(new Date(estimate.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Declined estimate — prominent Edit & Resend at the top */}
          {estimate.status === 'declined' && (
            <div className="w-full bg-gradient-to-r from-red-50 to-amber-50 border border-red-200 rounded-2xl p-4 mb-2" data-testid="declined-banner">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-red-900">Client declined this estimate</p>
                  {estimate.decline_reason && (
                    <p className="text-sm text-red-800 mt-1 whitespace-pre-wrap">
                      <span className="font-medium">Reason:</span> {estimate.decline_reason}
                    </p>
                  )}
                  {estimate.declined_at && (
                    <p className="text-xs text-red-700 mt-1">
                      Declined on {new Date(estimate.declined_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => navigate(`/estimates/${estimateId}/edit?revise=1`)}
                  className="bg-amber-500 hover:bg-amber-600 text-white flex-1"
                  data-testid="edit-and-resend-top-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit & Resend as Revised
                </Button>
                <Button
                  onClick={handleSendEstimate}
                  disabled={isSending}
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-50 flex-1"
                  data-testid="resend-without-edit-btn"
                >
                  {isSending ? (
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Resend As-Is
                </Button>
              </div>
            </div>
          )}
          
          {/* Edit button - available for draft estimates */}
          {estimate.status === 'draft' && (
            <PermissionGate permission="edit_draft_estimates">
              <Button
                onClick={() => navigate(`/estimates/${estimateId}/edit`)}
                className="btn-primary"
                data-testid="edit-estimate-btn"
              >
                <Edit2 className="w-5 h-5 mr-2" />
                Edit Estimate
              </Button>
            </PermissionGate>
          )}
          
          {estimate.status === 'draft' && (
            <PermissionGate permission="send_estimates">
              <Button
                onClick={handleSendEstimate}
                disabled={isSending}
                className="btn-secondary"
                data-testid="send-estimate-btn"
              >
                {isSending ? (
                  <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Send className="w-5 h-5 mr-2" />
                )}
                {t('send_to_client')}
              </Button>
            </PermissionGate>
          )}
          
          {estimate.status === 'sent' && (
            <Button
              onClick={() => copyLink()}
              variant="outline"
              className="h-12"
              data-testid="copy-link-btn"
            >
              <Copy className="w-5 h-5 mr-2" />
              {t('copy_link')}
            </Button>
          )}
          
          {estimate.status === 'approved' && (
            <PermissionGate permission="create_jobs">
              <ApprovedEstimateActions
                estimate={estimate}
                navigate={navigate}
                setShowConvertToJobModal={setShowConvertToJobModal}
                handleOpenResendModal={handleOpenResendModal}
                handleConvertToInvoice={handleConvertToInvoice}
                getDeclinedItems={getDeclinedItems}
                t={t}
              />
              {/* Split & Combine buttons for approved estimates with items */}
              {(estimate.line_items?.length > 1 || estimate.simple_pricing?.length > 1 || estimate.summary_sections?.length > 1) && (
                <Button
                  onClick={() => setShowSplitModal(true)}
                  variant="outline"
                  className="h-12 text-violet-600 border-violet-200 hover:bg-violet-50"
                  data-testid="split-estimate-btn"
                >
                  <Scissors className="w-5 h-5 mr-2" />
                  Split
                </Button>
              )}
              <Button
                onClick={() => setShowCombineModal(true)}
                variant="outline"
                className="h-12 text-purple-600 border-purple-200 hover:bg-purple-50"
                data-testid="combine-estimates-btn"
              >
                <Merge className="w-5 h-5 mr-2" />
                Combine
              </Button>
            </PermissionGate>
          )}
          
          <Button
            onClick={() => setShowPdfPreview(true)}
            variant="outline"
            className="h-12"
            data-testid="preview-pdf-btn"
          >
            <Eye className="w-5 h-5 mr-2" />
            {t('preview_pdf')}
          </Button>
          
          <Button
            onClick={handleDownloadPdf}
            variant="outline"
            className="h-12"
            data-testid="download-pdf-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            {t('download_pdf')}
          </Button>
          
          <Button
            onClick={handleDuplicate}
            variant="outline"
            className="h-12"
            data-testid="duplicate-estimate-btn"
          >
            <CopyPlus className="w-5 h-5 mr-2" />
            Duplicate
          </Button>
          
          <PermissionGate permission="delete_records">
            <Button
              onClick={handleDelete}
              variant="outline"
              className="h-12 text-destructive hover:bg-destructive hover:text-white"
              data-testid="delete-estimate-btn"
            >
              <Trash2 className="w-5 h-5" />
            </Button>
          </PermissionGate>
        </div>
      </div>
      
      {/* Public Link Banner */}
      {(estimate.status === 'sent' || publicUrl) && (
        <div className="card-highlight flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link2 className="w-6 h-6 text-blue-600" />
            <div>
              <p className="font-semibold">{t('client_approval_link')}</p>
              <p className="text-sm text-muted-foreground">
                {t('share_link_desc')}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => copyLink()}
              variant="outline"
              size="sm"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('copy')}
            </Button>
            <Button
              onClick={() => window.open(`/estimate/${estimate.public_view_token}`, '_blank')}
              variant="outline"
              size="sm"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('preview')}
            </Button>
          </div>
        </div>
      )}
      
      {/* Revision Banner - show if this is a revision of another estimate */}
      {estimate.parent_estimate_id && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <CopyPlus className="w-6 h-6 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-800">Revision Estimate</p>
              <p className="text-sm text-amber-700">
                This is a revision of estimate #{estimate.parent_estimate_id.slice(0, 8).toUpperCase()}
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/estimates/${estimate.parent_estimate_id}`)}
            variant="outline"
            size="sm"
            className="text-amber-700 border-amber-300 hover:bg-amber-100"
          >
            <Eye className="w-4 h-4 mr-2" />
            View Original
          </Button>
        </div>
      )}
      
      {/* Linked Job Banner */}
      {linkedJob && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-blue-900">Converted to Job</p>
              <p className="text-sm text-blue-700">
                Job #{linkedJob.id?.slice(0, 8).toUpperCase()} • {linkedJob.status?.replace('_', ' ').toUpperCase()}
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/jobs/${linkedJob.id}`)}
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            <Calendar className="w-4 h-4 mr-2" />
            View Job
          </Button>
        </div>
      )}
      
      {/* Split Jobs Banner */}
      {estimate.converted_to_job_ids?.length > 1 && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-4" data-testid="split-jobs-banner">
          <div className="flex items-center gap-3 mb-2">
            <Scissors className="w-5 h-5 text-violet-600" />
            <p className="font-semibold text-violet-900">Split into {estimate.converted_to_job_ids.length} Jobs</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {estimate.converted_to_job_ids.map((jobId, idx) => (
              <Button
                key={jobId}
                variant="outline"
                size="sm"
                onClick={() => navigate(`/jobs/${jobId}`)}
                className="border-violet-300 text-violet-700 hover:bg-violet-100"
                data-testid={`split-job-link-${idx}`}
              >
                <Briefcase className="w-3 h-3 mr-1" />
                Job #{jobId.slice(0, 8).toUpperCase()}
              </Button>
            ))}
          </div>
        </div>
      )}
      
      {/* Split Invoices Banner */}
      {estimate.converted_to_invoice_ids?.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="split-invoices-banner">
          <div className="flex items-center gap-3 mb-2">
            <Scissors className="w-5 h-5 text-emerald-600" />
            <p className="font-semibold text-emerald-900">Split into {estimate.converted_to_invoice_ids.length} Invoices</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {estimate.converted_to_invoice_ids.map((invId, idx) => (
              <Button
                key={invId}
                variant="outline"
                size="sm"
                onClick={() => navigate(`/invoices/${invId}`)}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                data-testid={`split-invoice-link-${idx}`}
              >
                <Receipt className="w-3 h-3 mr-1" />
                Invoice #{invId.slice(0, 8).toUpperCase()}
              </Button>
            ))}
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client & Job Info */}
          <div className="card-default">
            <h2 className="font-heading text-xl font-semibold mb-4">
              {estimate.project_title || 'Job Details'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Customer</p>
                <p className="font-semibold text-lg">{client?.name}</p>
                {client?.email && <p className="text-muted-foreground">{client.email}</p>}
                {client?.phone && <p className="text-muted-foreground">{client.phone}</p>}
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Service</p>
                <p className="font-semibold text-lg">{estimate.service_type}</p>
                {estimate.job_address && (
                  <p className="text-muted-foreground">{estimate.job_address}</p>
                )}
              </div>
            </div>
            {/* Work Types Tags - only show if populated */}
            {estimate.work_types && estimate.work_types.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-muted-foreground mb-2">Work Includes</p>
                <div className="flex flex-wrap gap-2">
                  {estimate.work_types.map((type, idx) => (
                    <span 
                      key={idx} 
                      className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Content based on display_mode */}
          {estimate.display_mode === 'simple' ? (
            /* Simple Mode: Materials, Process, Work Items */
            <div className="card-default space-y-6">
              {/* Scope of Work (Materials + Process) */}
              {(estimate.simple_materials || estimate.simple_process) && (
                <div>
                  <h2 className="font-heading text-xl font-semibold mb-4">Scope of Work</h2>
                  
                  {estimate.simple_materials && (
                    <div className="mb-4">
                      <h3 className="font-medium text-sm text-muted-foreground mb-2">Materials</h3>
                      <p className="text-foreground whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                        {estimate.simple_materials}
                      </p>
                    </div>
                  )}
                  
                  {estimate.simple_process && (
                    <div>
                      <h3 className="font-medium text-sm text-muted-foreground mb-2">Process</h3>
                      <p className="text-foreground whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                        {estimate.simple_process}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Work Items (Pricing) */}
              <div>
                <h2 className="font-heading text-xl font-semibold mb-4">Work Items</h2>
                <div className="space-y-3">
                  {estimate.simple_pricing?.map((item, idx) => {
                    const isApproved = item.is_approved !== false; // Default to approved if not set
                    const isDeclined = item.is_approved === false;
                    
                    return (
                      <div 
                        key={item.id || idx} 
                        className={`flex justify-between items-center p-4 rounded-lg border-2 ${
                          isDeclined 
                            ? 'bg-red-50 border-red-200 opacity-60' 
                            : isApproved && estimate.status === 'approved'
                              ? 'bg-green-50 border-green-200'
                              : 'bg-gray-50 border-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {estimate.status === 'approved' && (
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                              isDeclined 
                                ? 'bg-red-500 text-white' 
                                : 'bg-green-500 text-white'
                            }`}>
                              {isDeclined ? '✕' : '✓'}
                            </span>
                          )}
                          <span className={`font-medium ${isDeclined ? 'line-through text-gray-500' : ''}`}>
                            {item.title || `Item ${idx + 1}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`font-bold text-lg ${isDeclined ? 'text-gray-400 line-through' : ''}`}>
                            ${(item.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </span>
                          {isDeclined && (
                            <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Declined</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Totals */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between py-2">
                    <span>{t('subtotal')}</span>
                    <span>${estimate.subtotal?.toFixed(2)}</span>
                  </div>
                  {estimate.tax_amount > 0 && (
                    <div className="flex justify-between py-2">
                      <span>{t('tax')} ({estimate.tax_rate}%)</span>
                      <span>${estimate.tax_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 text-xl font-bold border-t border-gray-200">
                    <span>{t('total')}</span>
                    <span>${estimate.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Detailed/Summary/Sections Mode: Line Items Table */
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-4">Line Items</h2>
              
              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 font-medium">Item</th>
                      <th className="text-right py-3 font-medium w-20">Qty</th>
                      <th className="text-right py-3 font-medium w-24">Rate</th>
                      <th className="text-right py-3 font-medium w-28">Total</th>
                    </tr>
                  </thead>
                  <LineItemsTableBody items={estimate.line_items || []} />
                </table>
              </div>
              
              {/* Mobile Card Layout */}
              <div className="sm:hidden space-y-3">
                {estimate.line_items?.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-semibold">{item.name}</p>
                        {item.description && (
                          <p className="text-sm text-muted-foreground">{item.description}</p>
                        )}
                      </div>
                      <p className="font-bold text-lg">${item.total?.toFixed(2)}</p>
                    </div>
                    <div className="flex gap-4 text-sm text-muted-foreground">
                      <span>{item.qty} {item.unit}</span>
                      <span>×</span>
                      <span>${item.rate?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Totals */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between py-2">
                  <span>{t('subtotal')}</span>
                  <span>${estimate.subtotal?.toFixed(2)}</span>
                </div>
                {estimate.tax_amount > 0 && (
                  <div className="flex justify-between py-2">
                    <span>{t('tax')} ({estimate.tax_rate}%)</span>
                    <span>${estimate.tax_amount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between py-3 text-xl font-bold border-t border-gray-200">
                  <span>{t('total')}</span>
                  <span>${estimate.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Timeline */}
          <div className="card-default">
            <h2 className="font-heading text-xl font-semibold mb-4">Status</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Created</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(estimate.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              {estimate.sent_at && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Send className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Sent</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(estimate.sent_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
              
              {estimate.approved_at && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Approved</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(estimate.approved_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
              
              {estimate.status === 'declined' && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                    <XCircle className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium">Declined</p>
                    {estimate.declined_at && (
                      <p className="text-sm text-muted-foreground">
                        {new Date(estimate.declined_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Follow-Up Reminders — for sent estimates */}
          {estimate.status === 'sent' && organization?.feature_flags?.follow_ups && (
            <FollowUpPanel estimate={estimate} onUpdate={loadEstimate} />
          )}

          {/* Customer Q&A — visible once estimate is sent */}
          {(estimate.status === 'sent' || estimate.status === 'approved' || estimate.status === 'declined') && (
            <EstimateQA
              mode="contractor"
              orgId={organization?.id}
              estimateId={estimate.id}
              defaultAuthorName={user?.name || ''}
              defaultAuthorEmail={user?.email || ''}
              primaryColor={organization?.brand_primary_color || '#1e3a8a'}
              user={user}
              customerName={client?.name}
            />
          )}
          
          {/* Internal Notes */}
          {estimate.internal_notes && (
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-4">Internal Notes</h2>
              <p className="text-muted-foreground">{estimate.internal_notes}</p>
            </div>
          )}
          
          {/* Measurements */}
          {estimate.measurements && Object.keys(estimate.measurements).some(k => estimate.measurements[k]) && (
            <div className="card-default">
              <h2 className="font-heading text-xl font-semibold mb-4">Measurements</h2>
              <div className="space-y-2 text-sm">
                {estimate.measurements.area_sqft && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Area:</span>
                    <span>{estimate.measurements.area_sqft}</span>
                  </div>
                )}
                {estimate.measurements.timeline && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Timeline:</span>
                    <span>{estimate.measurements.timeline}</span>
                  </div>
                )}
                {estimate.measurements.materials && (
                  <div>
                    <span className="text-muted-foreground">Materials:</span>
                    <p className="mt-1">{estimate.measurements.materials}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Photos */}
          <div className="card-default">
            <PhotoGallery
              entityType="estimate"
              entityId={estimateId}
              photos={estimate.photos || []}
              onPhotosChange={(newPhotos) => {
                setEstimate({ ...estimate, photos: newPhotos });
              }}
            />
          </div>
        </div>
      </div>
      
      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={showPdfPreview}
        onClose={() => setShowPdfPreview(false)}
        pdfUrl={organization?.id && estimateId ? `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/estimates/${estimateId}/pdf` : null}
        title="Estimate Preview"
        documentNumber={estimateId?.slice(0, 8).toUpperCase()}
      />
      
      {/* Convert to Job Modal */}
      <Dialog open={showConvertToJobModal} onOpenChange={setShowConvertToJobModal}>
        <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              Create Job from Estimate
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-3 sm:py-4 space-y-3 sm:space-y-4">
            <p className="text-sm text-muted-foreground">
              This will create a new job linked to this estimate. The job will appear on your calendar.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Client:</span>
                <span className="font-medium truncate ml-2">{client?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Service:</span>
                <span className="font-medium truncate ml-2">{estimate?.service_type}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total:</span>
                <span className="font-medium">${estimate?.total?.toFixed(2)}</span>
              </div>
            </div>
            
            <div>
              <Label htmlFor="job-start-date" className="text-sm">Job Start Date (optional)</Label>
              <Input
                id="job-start-date"
                type="date"
                value={jobStartDate}
                onChange={(e) => setJobStartDate(e.target.value)}
                className="mt-1 h-10"
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave blank to use estimate's date or today
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowConvertToJobModal(false)} className="h-10 w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleConvertToJob} className="bg-blue-600 hover:bg-blue-700 h-10 w-full sm:w-auto">
              <Briefcase className="w-4 h-4 mr-2" />
              Create Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Resend Declined Items Modal */}
      <Dialog open={showResendModal} onOpenChange={setShowResendModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CopyPlus className="w-5 h-5 text-amber-600" />
              Resend Declined Items
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Create a new revision estimate with the declined items. You can edit the items before sending.
            </p>
            
            {/* Editable items */}
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {resendItems.map((item, idx) => (
                <div key={item.id || idx} className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                  <div>
                    <Label className="text-xs text-amber-700">Item Name</Label>
                    <Input
                      value={item.title}
                      onChange={(e) => updateResendItem(idx, 'title', e.target.value)}
                      className="mt-1 h-9"
                      placeholder="Item name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-amber-700">Price ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.price}
                      onChange={(e) => updateResendItem(idx, 'price', e.target.value)}
                      className="mt-1 h-9"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Total */}
            <div className="flex justify-between pt-3 border-t text-lg font-bold">
              <span>Total</span>
              <span>
                ${resendItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0).toFixed(2)}
              </span>
            </div>
            
            <p className="text-xs text-muted-foreground bg-blue-50 p-3 rounded">
              This will create a new estimate linked to the original. When approved, you can merge it back or keep separate.
            </p>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowResendModal(false)} className="h-10 w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRevisionEstimate} 
              disabled={isCreatingRevision || resendItems.length === 0}
              className="bg-amber-600 hover:bg-amber-700 h-10 w-full sm:w-auto"
            >
              {isCreatingRevision ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <CopyPlus className="w-4 h-4 mr-2" />
              )}
              Create Revision Estimate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Confirm Dialog */}
      {ConfirmDialogComponent}
      
      {/* Split Estimate Modal */}
      <SplitEstimateModal
        open={showSplitModal}
        onOpenChange={setShowSplitModal}
        estimate={estimate}
        onComplete={loadEstimate}
      />
      
      {/* Combine Estimates Modal */}
      <CombineEstimatesModal
        open={showCombineModal}
        onOpenChange={setShowCombineModal}
        clientId={estimate?.client_id}
        clientName={client?.name}
        onComplete={loadEstimate}
      />
    </div>
  );
};

export default EstimateDetailPage;
